import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { FaIcon } from '../../FaIcon';
import { getPrice, formatPrice } from '../pricingConfig';
import { APIClient } from '../../../utils/APIClient';
import { API_URL } from '../../../settings/local_settings';
import { LocalStorage } from '../../../utils/LocalStorage';

export function McpUpgradePlanCard({ plan, currency, period }) {
    const { t } = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);

    const price = getPrice(plan, currency, period);
    const formattedPrice = formatPrice(price, currency);
    const planName = t(plan.nameKey);

    const handleCTAClick = (e) => {
        e.preventDefault();
        setShowModal(true);
        setError(null);
    };

    const handleConfirm = () => {
        setLoading(true);
        setError(null);

        new APIClient(`${API_URL}agency/subscription/mcp-upgrade/`)
            .call('POST', { plan: plan.id })
            .then((response) => {
                setLoading(false);
                if (response.status === 'OK') {
                    setSuccess(true);
                    LocalStorage.updateIsDashboMcp(false);
                } else {
                    setError(response.error_message || t('mcp_upgrade.pricing.error_generic'));
                }
            })
            .catch(() => {
                setLoading(false);
                setError(t('mcp_upgrade.pricing.error_generic'));
            });
    };

    const handleCloseModal = () => {
        if (!loading) {
            setShowModal(false);
            setError(null);
        }
    };

    const handleGoToDashbo = () => {
        window.location.reload();
    };

    return (
        <div className={`pricing-card ${plan.highlighted ? '-highlighted' : ''}`}>
            {plan.badgeKey && (
                <div className="pricing-card-badge">{t(plan.badgeKey)}</div>
            )}
            <div className="pricing-card-header">
                <h3 className="pricing-card-name">{planName}</h3>
                <p className="pricing-card-description">{t(plan.descriptionKey)}</p>
            </div>
            <div className="pricing-card-price">
                <span className="price-amount">$ {formattedPrice}</span>
                <span className="price-period">/{t('mcp_upgrade.pricing.per_month')}</span>
                {currency === 'ARS' && <span className="price-iva"> + IVA</span>}
            </div>
            <button type="button" className={`btn ${plan.highlighted ? '-alt' : '-alt -negative'} pricing-card-cta`} onClick={handleCTAClick}>
                {t('mcp_upgrade.pricing.cta')}
                <FaIcon iconName="arrow-right" />
            </button>
            <ul className="pricing-card-features">
                {plan.features.map((feat) => (
                    <li key={feat.key} className={feat.included ? 'feature-included' : 'feature-excluded'}>
                        <FaIcon iconName={feat.included ? 'check' : 'xmark'} className={`feature-icon ${feat.included ? '-yes' : '-no'}`} />
                        <span>{t(feat.labelKey)}</span>
                    </li>
                ))}
            </ul>

            <Modal isOpen={showModal} toggle={handleCloseModal}>
                <ModalHeader toggle={handleCloseModal}>
                    {t('mcp_upgrade.pricing.confirm_modal.title', { plan: planName })}
                </ModalHeader>
                <ModalBody>
                    <div className="modal-section">
                        {success ? (
                            <div style={{ textAlign: 'center', padding: '1em 0' }}>
                                <FaIcon iconName="circle-check" style={{ color: '#7CB502', fontSize: '3em', marginBottom: '0.5em' }} />
                                <p>{t('mcp_upgrade.pricing.success_message', { plan: planName })}</p>
                            </div>
                        ) : (
                            <>
                                <p>{t('mcp_upgrade.pricing.confirm_modal.message', { plan: planName })}</p>
                                <p>{t('mcp_upgrade.pricing.confirm_modal.trial_note')}</p>
                                {error && (
                                    <p style={{ color: '#DF0D00', marginTop: '0.5em' }}>{error}</p>
                                )}
                            </>
                        )}
                    </div>
                </ModalBody>
                <ModalFooter>
                    {success ? (
                        <button type="button" className="btn -alt" onClick={handleGoToDashbo}>
                            {t('mcp_upgrade.pricing.success_cta')}
                        </button>
                    ) : (
                        <>
                            <button type="button" className="btn -negative" onClick={handleCloseModal} disabled={loading}>
                                {t('mcp_upgrade.pricing.confirm_modal.cancel')}
                            </button>
                            <button type="button" className="btn -alt" onClick={handleConfirm} disabled={loading}>
                                {loading && <FaIcon iconName="spinner" spin style={{ marginRight: '0.5em' }} />}
                                {t('mcp_upgrade.pricing.confirm_modal.confirm')}
                            </button>
                        </>
                    )}
                </ModalFooter>
            </Modal>
        </div>
    );
}
